import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { IconCategory, MapArrow, MapMode, MapObject } from '@warplan/shared';
import { api } from '../lib/api-client.js';
import { useAuth } from '../lib/auth-store.js';
import { useRealtime, usePresence } from '../lib/realtime.js';
import { useMapView } from '../state/map-view-store.js';
import { ATLAS, REGION_LABELS, TILE_W, TILE_H, findRegionKey } from '../lib/foxhole-maps.js';
import { getFoxholeIconUrl } from '../lib/foxhole-icon-map.js';
import {
  useObjectsSource as useMapObjects,
  useCreateObjectSource as useCreateMapObject,
  usePatchObjectSource as usePatchMapObject,
  useDeleteObjectSource as useDeleteMapObject,
  useArrowsSource as useMapArrows,
  useCreateArrowSource as useCreateMapArrow,
  usePatchArrowSource as usePatchMapArrow,
  useDeleteArrowSource as useDeleteMapArrow,
  useWarPlanSource as useWarPlan,
  useOperationPlanSource as useOperationPlan,
} from '../lib/map-data-source.js';
import { useMapSettings, useUpdateMapSettings } from '../lib/map-settings-api.js';
import { getMapFlavour, isSandbox } from '../lib/view-mode.js';
import {
  FoxholeAtlas,
  type AtlasView,
  type FoxholeAtlasHandle,
} from '../components/FoxholeAtlas.js';
import { MapTopbar } from '../components/MapTopbar.js';
import {
  MapToolbar,
  type PlaceMode,
  type ArrowDrawOptions,
} from '../components/MapToolbar.js';
import { MapMarkers, EDGE_ZONE_PX } from '../components/MapMarkers.js';
import { SubregionsLayer } from '../components/SubregionsLayer.js';
import { resolveDragCollision } from '../lib/resolve-drag-collision.js';
import {
  resolveIconCollisions,
  iconCollisionSize,
} from '../lib/resolve-icon-collisions.js';
import {
  MapArrows,
  hitTestArrows,
  parseArrowSyntheticId,
  isArrowSyntheticId,
  type DraftArrow,
  type VertexDragInfo,
} from '../components/MapArrows.js';
import { MapArrowPopover } from '../components/MapArrowPopover.js';
import { MapContextMenu } from '../components/MapContextMenu.js';
import { MapObjectPopover } from '../components/MapObjectPopover.js';
import { HelpModal } from '../components/HelpModal.js';
import { WarPlanPanel } from '../components/WarPlanPanel.js';
import { WarPlanExportModal } from '../components/WarPlanExportModal.js';
import { FacBuildMsupExportModal } from '../components/FacBuildMsupExportModal.js';
import { OperationPlanPanel } from '../components/OperationPlanPanel.js';
import { OperationExportModal } from '../components/OperationExportModal.js';
import { AssetListPanel } from '../components/AssetListPanel.js';
import { useAssetPanel } from '../state/asset-panel-store.js';
import { useActiveMode } from '../state/active-mode-store.js';
import { useSandboxRegionStore } from '../state/sandbox-region-store.js';
import { usePrioritySync } from '../lib/priority-sync.js';
import { useObjectiveSync } from '../lib/objective-sync.js';
import { SandboxNotification } from '../components/SandboxNotification.js';

/**
 * Categories and icon IDs that the `editor` role cannot create/modify/delete.
 * Mirrors the backend constants in map.routes.ts.
 */
const EDITOR_FORBIDDEN_CATEGORIES = new Set<IconCategory>(['combat']);
const EDITOR_FORBIDDEN_ICON_IDS = new Set<string>([
  'LOGISTIC_HUB',
  'LOGISTIC_SEAPORT',
  'LOGISTIC_STORAGEDEPOT',
  'LOGISTIC_AIRWAY',
]);

interface FoxholeWarItem {
  iconType: number;
  teamId: string | null;
  x: number;
  y: number;
}

interface FoxholeTextItem {
  text: string;
  x: number;
  y: number;
  mapMarkerType: string;
}

interface FoxholeMapIcon {
  iconType: number;
  teamId: string | null;
  wx: number;
  wy: number;
}

interface FoxholeWarLabel {
  wx: number;
  wy: number;
  text: string;
  kind: 'major' | 'minor';
}

interface DynamicAllResponse {
  fetchedAt: string;
  offline: boolean;
  maps: Array<{ mapName: string; mapItems: FoxholeWarItem[] }>;
}

interface StaticAllResponse {
  fetchedAt: string;
  offline: boolean;
  maps: Array<{ mapName: string; mapTextItems: FoxholeTextItem[] }>;
}

export function MapPage(): React.JSX.Element {
  useRealtime();
  const presence = usePresence();
  const account = useAuth((s) => s.account);
  const isAdmin = account?.role === 'admin';
  const isEditor = account?.role === 'editor';
  const canEditLive = isAdmin || isEditor;

  const viewMode = useActiveMode((s) => s.viewMode);
  const lastSandboxFlavour = useActiveMode((s) => s.lastSandboxFlavour);
  const setViewMode = useActiveMode((s) => s.setViewMode);
  /**
   * `activeMode` is the underlying MapMode (icon taxonomy + panel flavour),
   * shared between live and sandbox views.
   */
  const activeMode: MapMode = getMapFlavour(viewMode);
  const sandboxActive = isSandbox(viewMode);
  /**
   * In sandbox modes, every authenticated user can edit anything regardless
   * of role (User/Editor/Admin all welcome). Local-only state means no
   * shared-resource RBAC concern.
   */
  const canEditSandbox = sandboxActive && !!account;
  const canEdit = sandboxActive ? canEditSandbox : canEditLive;

  /**
   * Categories/icons that the editor role is forbidden from creating in the
   * CURRENT mode. In planner mode, the legacy planner restrictions apply.
   * In operation mode, editors have full freedom. In sandbox modes, there
   * are no restrictions for any role.
   */
  const forbiddenCategoriesForMode: Set<IconCategory> | undefined =
    !sandboxActive && isEditor && activeMode === 'planner'
      ? EDITOR_FORBIDDEN_CATEGORIES
      : undefined;
  const forbiddenIconIdsForMode: Set<string> | undefined =
    !sandboxActive && isEditor && activeMode === 'planner'
      ? EDITOR_FORBIDDEN_ICON_IDS
      : undefined;

  /** Returns true if the current user may mutate this specific map object. */
  const canEditObject = useCallback(
    (obj: MapObject): boolean => {
      if (sandboxActive) return canEditSandbox;
      if (isAdmin) return true;
      if (!isEditor) return false;
      // Editors have free hand on operation-mode content.
      if (obj.mode === 'operation') return true;
      return obj.kind !== 'combat' && !EDITOR_FORBIDDEN_ICON_IDS.has(obj.metadata.iconId);
    },
    [sandboxActive, canEditSandbox, isAdmin, isEditor],
  );

  const mapBackground = useMapView((s) => s.mapBackground);
  const setMapBackground = useMapView((s) => s.setMapBackground);
  const toggleInkBackground = useMapView((s) => s.toggleInkBackground);
  const showGrid = useMapView((s) => s.showGrid);
  const toggleGrid = useMapView((s) => s.toggleGrid);
  const showSubregions = useMapView((s) => s.showSubregions);
  const toggleSubregions = useMapView((s) => s.toggleSubregions);
  const setViewport = useMapView((s) => s.setViewport);

  const assetPanelOpen = useAssetPanel((s) => s.isOpen);
  // left-2 (8px) + panel width or collapsed button width (40px) + gap (8px)
  const toolbarLeft = assetPanelOpen ? 8 + 300 + 8 : 8 + 40 + 8;
  const initialViewport = useRef(useMapView.getState().viewport);

  const [helpOpen, setHelpOpen] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [facExportOpen, setFacExportOpen] = useState(false);
  const [msupExportOpen, setMsupExportOpen] = useState(false);
  const [operationExportOpen, setOperationExportOpen] = useState(false);
  const [placeMode, setPlaceMode] = useState<PlaceMode | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [popover, setPopover] = useState<{ x: number; y: number; object: MapObject } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [pendingDeleteArrow, setPendingDeleteArrow] = useState<string | null>(null);

  // Arrow state.
  const [arrowOptions, setArrowOptions] = useState<ArrowDrawOptions | null>(null);
  const [draftPoints, setDraftPoints] = useState<Array<{ x: number; y: number }>>([]);
  const [cursorWorld, setCursorWorld] = useState<{ x: number; y: number } | null>(null);
  const [selectedArrowId, setSelectedArrowId] = useState<string | null>(null);
  const [arrowPopover, setArrowPopover] = useState<
    { x: number; y: number; arrow: MapArrow } | null
  >(null);
  const [vertexDrag, setVertexDrag] = useState<VertexDragInfo | null>(null);
  /**
   * Arrow body drag state. Set on hit-test (we capture the starting world
   * position + the original points snapshot) and consumed on `onMarkerMoved`.
   * Live preview is mirrored into `bodyDragPreview` so the SVG layer can
   * translate the polyline while dragging.
   */
  const bodyDragRef = useRef<{
    arrowId: string;
    startX: number;
    startY: number;
    originalPoints: Array<{ x: number; y: number }>;
  } | null>(null);
  const [bodyDragPreview, setBodyDragPreview] = useState<
    { arrowId: string; deltaX: number; deltaY: number } | null
  >(null);

  /**
   * `placeMode`/`arrowOptions` are armed from a category that belongs to
   * whichever mode was active at selection time (planner vs operation icon
   * palette, live vs sandbox destination). Switching `viewMode` in ANY
   * direction must clear them:
   * - crossing planner ↔ operation (or their sandbox variants) could place
   *   an object whose `kind` category and `mode` disagree (see
   *   `getCategoryMode`);
   * - crossing live ↔ sandbox at the SAME flavour (e.g. `planner` ↔
   *   `sandbox-planner`) keeps `kind`/`mode` consistent, but silently
   *   changes the write destination (REST API vs local sandbox store) and
   *   the RBAC restrictions applied (sandbox has none) between the moment
   *   the icon was picked and the moment it's placed — `onMapClick` never
   *   re-validates `forbiddenCategories`/`forbiddenIconIds` against the
   *   CURRENT mode, so a category picked while unrestricted (sandbox) must
   *   not survive into a restricted context (live) or vice versa.
   * `viewMode` (not just `activeMode`) is therefore the correct dependency.
   */
  useEffect(() => {
    setPlaceMode(null);
    setArrowOptions(null);
    setDraftPoints([]);
    setCursorWorld(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const [ctxMenu, setCtxMenu] = useState<{
    x: number;
    y: number;
    worldX: number;
    worldY: number;
  } | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const atlasRef = useRef<FoxholeAtlasHandle | null>(null);
  const [size, setSize] = useState({ w: 800, h: 600 });
  const persistTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const objectsQuery = useMapObjects();
  const createMut = useCreateMapObject();
  const patchMut = usePatchMapObject();
  const deleteMut = useDeleteMapObject();

  const arrowsQuery = useMapArrows();
  const createArrowMut = useCreateMapArrow();
  const patchArrowMut = usePatchMapArrow();
  const deleteArrowMut = useDeleteMapArrow();

  const mapSettingsQuery = useMapSettings();
  const updateSettingsMut = useUpdateMapSettings();
  const warPlanQuery = useWarPlan();
  const liveDisabledRegionKeys = mapSettingsQuery.data?.disabledRegionKeys;

  /**
   * In sandbox mode, hexagon activation is a per-user local override so
   * anyone can freely enable/disable regions to work on without touching the
   * shared admin config. It is seeded once from the live value so sandbox
   * starts from the same picture as the live map, then diverges freely.
   * Leaving sandbox always shows the real, admin-controlled regions again.
   */
  const sandboxDisabledRegionKeys = useSandboxRegionStore((s) => s.disabledRegionKeys);
  const initSandboxRegionsFromLive = useSandboxRegionStore((s) => s.initFromLive);
  const toggleSandboxRegion = useSandboxRegionStore((s) => s.toggleRegion);
  useEffect(() => {
    if (liveDisabledRegionKeys) initSandboxRegionsFromLive(liveDisabledRegionKeys);
  }, [liveDisabledRegionKeys, initSandboxRegionsFromLive]);

  const disabledSet = useMemo(
    () =>
      new Set(sandboxActive ? sandboxDisabledRegionKeys : (liveDisabledRegionKeys ?? [])),
    [sandboxActive, sandboxDisabledRegionKeys, liveDisabledRegionKeys],
  );

  const allObjects: MapObject[] = useMemo(
    () => (objectsQuery.data?.objects ?? []) as MapObject[],
    [objectsQuery.data],
  );

  const operationPlanQuery = useOperationPlan();
  usePrioritySync(allObjects, operationPlanQuery.data ?? null);
  useObjectiveSync(allObjects, operationPlanQuery.data ?? null);

  const allArrows: MapArrow[] = useMemo(
    () => (arrowsQuery.data?.arrows ?? []) as MapArrow[],
    [arrowsQuery.data],
  );

  /** Objects/arrows filtered to the currently active mode. */
  const objects: MapObject[] = useMemo(
    () => allObjects.filter((o) => o.mode === activeMode),
    [allObjects, activeMode],
  );
  const arrows: MapArrow[] = useMemo(
    () => allArrows.filter((a) => a.mode === activeMode),
    [allArrows, activeMode],
  );

  /** Count of operation-mode objects and arrows for the "New operation" button. */
  const operationObjectCount = useMemo(
    () => allObjects.filter((o) => o.mode === 'operation').length,
    [allObjects],
  );
  const operationArrowCount = useMemo(
    () => allArrows.filter((a) => a.mode === 'operation').length,
    [allArrows],
  );

  /** Returns true if the current user may mutate this arrow. */
  const canEditArrow = useCallback(
    (arrow: MapArrow): boolean => {
      if (sandboxActive) return canEditSandbox;
      if (isAdmin) return true;
      if (!isEditor) return false;
      if (arrow.mode === 'operation') return true;
      return arrow.kind !== 'combat';
    },
    [sandboxActive, canEditSandbox, isAdmin, isEditor],
  );

  /** Objects whose region is not admin-disabled (used for rendering + hit-tests). */
  const visibleObjects: MapObject[] = useMemo(() => {
    if (disabledSet.size === 0) return objects;
    return objects.filter((o) => {
      const k = findRegionKey(o.position.x, o.position.y);
      return k === null || !disabledSet.has(k);
    });
  }, [objects, disabledSet]);

  /**
   * Arrows visible on the interactive map: filtered by per-kind visibility
   * toggles (AssetListPanel "Flèches" section) and by region (starting vertex
   * must be in a non-disabled hex, matching the export filter logic).
   */
  const arrowVisibility = useAssetPanel((s) => s.arrowVisibility);
  const visibleArrows: MapArrow[] = useMemo(() => {
    return arrows.filter((a) => {
      if (arrowVisibility[a.kind] === false) return false;
      if (disabledSet.size === 0) return true;
      const origin = a.points[0];
      if (!origin) return false;
      const k = findRegionKey(origin.x, origin.y);
      return k === null || !disabledSet.has(k);
    });
  }, [arrows, arrowVisibility, disabledSet]);

  /**
   * Arrows handed to the export modals. Pre-filtered only by per-kind
   * visibility (the modal handles region/hex filtering itself based on the
   * user's per-hex selections).
   */
  const arrowsForExports: MapArrow[] = useMemo(
    () => arrows.filter((a) => arrowVisibility[a.kind] !== false),
    [arrows, arrowVisibility],
  );

  // Keep popover synced if the underlying object updates (e.g. SSE).
  useEffect(() => {
    if (!popover) return;
    const fresh = objects.find((o) => o.id === popover.object.id);
    if (!fresh) {
      setPopover(null);
      setSelectedId((id) => (id === popover.object.id ? null : id));
      return;
    }
    if (fresh !== popover.object) {
      setPopover({ ...popover, object: fresh });
    }
  }, [objects, popover]);

  // Keep arrow popover synced with SSE updates / deletions.
  useEffect(() => {
    if (!arrowPopover) return;
    const fresh = arrows.find((a) => a.id === arrowPopover.arrow.id);
    if (!fresh) {
      setArrowPopover(null);
      setSelectedArrowId((id) => (id === arrowPopover.arrow.id ? null : id));
      return;
    }
    if (fresh !== arrowPopover.arrow) {
      setArrowPopover({ ...arrowPopover, arrow: fresh });
    }
  }, [arrows, arrowPopover]);

  // Delete key shortcut: trigger confirmation when popover is open.
  useEffect(() => {
    if (!popover || !canEditObject(popover.object)) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      // Ignore when typing in an input / textarea.
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      setPendingDelete(popover!.object.id);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [popover, canEdit, canEditObject]);

  // Arrow drafting keyboard shortcuts (Esc cancel, Enter confirm).
  useEffect(() => {
    if (!arrowOptions) return;
    function onKeyDown(e: KeyboardEvent): void {
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setArrowOptions(null);
        setDraftPoints([]);
        setCursorWorld(null);
      } else if (e.key === 'Enter') {
        if (!arrowOptions || draftPoints.length < 2) return;
        e.preventDefault();
        createArrowMut.mutate({
          kind: arrowOptions.kind,
          points: draftPoints,
          color: arrowOptions.color,
          arrowheadStart: arrowOptions.arrowheadStart,
          arrowheadEnd: arrowOptions.arrowheadEnd,
          dashed: arrowOptions.dashed,
          mode: activeMode,
        });
        setArrowOptions(null);
        setDraftPoints([]);
        setCursorWorld(null);
      } else if (e.key === 'Backspace' && draftPoints.length > 0) {
        e.preventDefault();
        setDraftPoints((prev) => prev.slice(0, -1));
      }
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [arrowOptions, draftPoints, createArrowMut, activeMode]);

  // Delete key for the selected arrow (when arrow popover is open).
  useEffect(() => {
    if (!arrowPopover || !canEditArrow(arrowPopover.arrow)) return;
    function onKeyDown(e: KeyboardEvent): void {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      e.preventDefault();
      setPendingDeleteArrow(arrowPopover!.arrow.id);
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [arrowPopover, canEdit, canEditArrow]);

  useEffect(() => {
    const el = stageRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      if (!e) return;
      const { width, height } = e.contentRect;
      setSize({ w: Math.max(100, Math.floor(width)), h: Math.max(100, Math.floor(height)) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const foxholeData = useQuery({
    queryKey: ['foxhole', 'all-dynamic'],
    queryFn: () => api.get<DynamicAllResponse>('/api/foxhole/dynamic/all'),
    staleTime: 25_000,
    refetchInterval: 30_000,
  });

  const foxholeStatic = useQuery({
    queryKey: ['foxhole', 'all-static'],
    queryFn: () => api.get<StaticAllResponse>('/api/foxhole/static/all'),
    staleTime: 60 * 60_000,
  });

  const atlasIndex = useMemo(() => new Map(ATLAS.map((t) => [t.mapName, t] as const)), []);

  const foxholeIcons = useMemo((): FoxholeMapIcon[] => {
    const result: FoxholeMapIcon[] = [];
    for (const { mapName, mapItems } of foxholeData.data?.maps ?? []) {
      const tile = atlasIndex.get(mapName);
      if (!tile) continue;
      for (const item of mapItems) {
        result.push({
          iconType: item.iconType,
          teamId: item.teamId,
          wx: item.x * TILE_W + tile.x - TILE_W / 2,
          wy: item.y * TILE_H + tile.y - TILE_H / 2,
        });
      }
    }
    return result;
  }, [foxholeData.data, atlasIndex]);

  const foxholeWarLabels = useMemo((): FoxholeWarLabel[] => {
    const result: FoxholeWarLabel[] = [];
    for (const { mapName, mapTextItems } of foxholeStatic.data?.maps ?? []) {
      const tile = atlasIndex.get(mapName);
      if (!tile) continue;
      for (const item of mapTextItems) {
        result.push({
          wx: item.x * TILE_W + tile.x - TILE_W / 2,
          wy: item.y * TILE_H + tile.y - TILE_H / 2,
          text: item.text,
          kind: item.mapMarkerType === 'Major' ? 'major' : 'minor',
        });
      }
    }
    return result;
  }, [foxholeStatic.data, atlasIndex]);

  const searchItems = useMemo(() => {
    const items: Array<{
      id: string;
      label: string;
      kind: 'region' | 'major' | 'minor';
      wx: number;
      wy: number;
    }> = [];
    for (const tile of ATLAS) {
      if (disabledSet.has(tile.key)) continue;
      items.push({
        id: `region:${tile.key}`,
        label: REGION_LABELS[tile.key] ?? tile.key,
        kind: 'region',
        wx: tile.x,
        wy: tile.y,
      });
    }
    foxholeWarLabels.forEach((label, i) => {
      const key = findRegionKey(label.wx, label.wy);
      if (key && disabledSet.has(key)) return;
      items.push({
        id: `${label.kind}:${i}:${label.text}`,
        label: label.text,
        kind: label.kind,
        wx: label.wx,
        wy: label.wy,
      });
    });
    return items;
  }, [foxholeWarLabels, disabledSet]);

  function onSearchSelect(item: {
    kind: 'region' | 'major' | 'minor';
    wx: number;
    wy: number;
  }): void {
    if (!atlasRef.current) return;
    const fitRegion = Math.min(size.w / TILE_W, size.h / TILE_H) * 0.95;
    const scale =
      item.kind === 'region'
        ? fitRegion
        : item.kind === 'major'
          ? Math.max(fitRegion * 2.2, 1.4)
          : Math.max(fitRegion * 3.5, 2.2);
    atlasRef.current.setView({ cx: item.wx, cy: item.wy, scale });
  }

  function onViewChange(v: AtlasView): void {
    if (persistTimer.current) clearTimeout(persistTimer.current);
    persistTimer.current = setTimeout(() => {
      setViewport({ cx: v.cx, cy: v.cy, scale: v.scale });
    }, 300);
  }

  const MIN_ICON_SCALE = 0.7;
  const MAX_ICON_SCALE = 2.3;

  /**
   * Cache of de-overlapped render positions, keyed by marker id.
   * Filled synchronously inside the overlay render function (where `iconInv`
   * is known) so that pointer-event callbacks fired afterwards can read the
   * same positions that are currently visible on screen.
   */
  const adjustedPositionsRef = useRef<Map<string, { x: number; y: number }>>(new Map());

  const overlay = (
    view: AtlasView,
    markerDrag?: { id: string; worldX: number; worldY: number } | null,
    resizeDrag?: { id: string; scale: number } | null,
  ): React.ReactNode => {
    const inv = 1 / Math.max(view.scale, 0.0001);
    const iconInv = Math.max(Math.min(inv, 1 / MIN_ICON_SCALE), 1 / MAX_ICON_SCALE);

    // Build the rect set for push-apart. The currently-dragged or -resized icon
    // is excluded: its visible position comes from the cursor (drag) or stays
    // anchored at its origin (resize), so we don't want global push-apart to
    // fight against those live values.
    const liveId =
      (markerDrag && !isArrowSyntheticId(markerDrag.id) ? markerDrag.id : null) ??
      (resizeDrag ? resizeDrag.id : null);
    const collisionRects = visibleObjects
      .filter((o) => o.id !== liveId)
      .map((o) => ({
        id: o.id,
        x: o.position.x,
        y: o.position.y,
        size: iconCollisionSize(iconInv, o.metadata.scale ?? 1),
      }));
    const adjusted = resolveIconCollisions(collisionRects);
    const nextMap = new Map<string, { x: number; y: number }>();
    for (const o of visibleObjects) {
      nextMap.set(o.id, adjusted.get(o.id) ?? o.position);
    }
    adjustedPositionsRef.current = nextMap;
    return (
      <>
        {showSubregions && (
          <SubregionsLayer icons={foxholeIcons} disabledRegionKeys={disabledSet} />
        )}
        {foxholeIcons.map((item, i) => {
          const url = getFoxholeIconUrl(item.iconType, item.teamId);
          if (!url) return null;
          const FOX_PX = 16;
          return (
            <image
              key={`fox-${i}`}
              href={url}
              x={item.wx - (FOX_PX / 2) * iconInv}
              y={item.wy - (FOX_PX / 2) * iconInv}
              width={FOX_PX * iconInv}
              height={FOX_PX * iconInv}
              preserveAspectRatio="xMidYMid meet"
              pointerEvents="none"
            />
          );
        })}
        <MapMarkers
          objects={visibleObjects}
          iconInv={iconInv}
          adjustedPositions={adjustedPositionsRef.current}
          selectedId={selectedId}
          {...(markerDrag && !isArrowSyntheticId(markerDrag.id)
            ? { draggingId: markerDrag.id, draggingPosition: { x: markerDrag.worldX, y: markerDrag.worldY } }
            : {})}
          {...(resizeDrag
            ? { resizingId: resizeDrag.id, resizingScale: resizeDrag.scale }
            : {})}
        />
        <MapArrows
          arrows={visibleArrows}
          selectedId={selectedArrowId}
          iconInv={iconInv}
          vertexDrag={
            markerDrag && isArrowSyntheticId(markerDrag.id)
              ? (() => {
                  const parsed = parseArrowSyntheticId(markerDrag.id);
                  if (parsed && parsed.kind === 'vertex' && parsed.index !== undefined) {
                    return {
                      arrowId: parsed.arrowId,
                      vertexIdx: parsed.index,
                      worldX: markerDrag.worldX,
                      worldY: markerDrag.worldY,
                    } satisfies VertexDragInfo;
                  }
                  return vertexDrag ?? null;
                })()
              : vertexDrag
          }
          bodyDrag={(() => {
            if (
              markerDrag &&
              isArrowSyntheticId(markerDrag.id) &&
              bodyDragRef.current &&
              parseArrowSyntheticId(markerDrag.id)?.kind === 'body'
            ) {
              return {
                arrowId: bodyDragRef.current.arrowId,
                deltaX: markerDrag.worldX - bodyDragRef.current.startX,
                deltaY: markerDrag.worldY - bodyDragRef.current.startY,
              };
            }
            return bodyDragPreview;
          })()}
          draft={
            arrowOptions
              ? ({
                  kind: arrowOptions.kind,
                  color: arrowOptions.color,
                  arrowheadStart: arrowOptions.arrowheadStart,
                  arrowheadEnd: arrowOptions.arrowheadEnd,
                  dashed: arrowOptions.dashed,
                  points: draftPoints,
                  cursor: cursorWorld,
                } satisfies DraftArrow)
              : null
          }
        />
      </>
    );
  };

  /** Called on clean click on map background (no marker under cursor). */
  function onMapClick(worldX: number, worldY: number): void {
    const regionKey = findRegionKey(worldX, worldY);
    if (regionKey && disabledSet.has(regionKey)) return;
    // Arrow drawing takes priority over icon placement.
    if (arrowOptions) {
      setDraftPoints((prev) => [...prev, { x: worldX, y: worldY }]);
      return;
    }
    if (placeMode) {
      createMut.mutate({
        kind: placeMode.category,
        position: { x: worldX, y: worldY },
        metadata: { iconId: placeMode.iconId },
        mode: activeMode,
      });
      setPlaceMode(null);
      return;
    }
    // Click on empty map → clear arrow selection.
    if (selectedArrowId) {
      setSelectedArrowId(null);
      setArrowPopover(null);
    }
  }

  /** Hit-test callback used by FoxholeAtlas in onPointerDown to detect marker drag/click. */
  function onMarkerHitTest(worldX: number, worldY: number, scale: number): string | null {
    // While drafting an arrow we want clicks to add points, not start drags.
    if (arrowOptions) return null;
    const regionKey = findRegionKey(worldX, worldY);
    if (regionKey && disabledSet.has(regionKey)) return null;
    // Arrows first (vertices > midpoints > body) so vertex handles take priority.
    const arrowHit = hitTestArrows(arrows, selectedArrowId, worldX, worldY, scale);
    if (arrowHit) {
      // Body hits: capture start position + original points for translation.
      const parsed = parseArrowSyntheticId(arrowHit.syntheticId);
      if (parsed && parsed.kind === 'body') {
        const a = arrows.find((arr) => arr.id === parsed.arrowId);
        if (a) {
          bodyDragRef.current = {
            arrowId: a.id,
            startX: worldX,
            startY: worldY,
            originalPoints: a.points.map((p) => ({ x: p.x, y: p.y })),
          };
        }
      } else {
        bodyDragRef.current = null;
      }
      return arrowHit.syntheticId;
    }
    bodyDragRef.current = null;
    const iconInv = Math.max(Math.min(1 / scale, 1 / MIN_ICON_SCALE), 1 / MAX_ICON_SCALE);
    let hit: MapObject | null = null;
    let bestDist = Infinity;
    for (const obj of visibleObjects) {
      const rpos = adjustedPositionsRef.current.get(obj.id) ?? obj.position;
      const hitRadius = iconCollisionSize(iconInv, obj.metadata.scale ?? 1) / 2;
      const dx = Math.abs(rpos.x - worldX);
      const dy = Math.abs(rpos.y - worldY);
      if (dx <= hitRadius && dy <= hitRadius) {
        const d = Math.hypot(dx, dy);
        if (d < bestDist) {
          bestDist = d;
          hit = obj;
        }
      }
    }
    return hit?.id ?? null;
  }

  /** Called when user clicks a marker without dragging. */
  function onMarkerClick(id: string, clientX: number, clientY: number): void {
    // Arrow synthetic ids.
    if (isArrowSyntheticId(id)) {
      const parsed = parseArrowSyntheticId(id);
      if (!parsed) return;
      const arrow = arrows.find((a) => a.id === parsed.arrowId);
      if (!arrow) return;
      if (parsed.kind === 'midpoint' && parsed.index !== undefined) {
        // Insert a new vertex between segment endpoints at the midpoint.
        if (!canEditArrow(arrow)) return;
        const i = parsed.index;
        const p = arrow.points[i]!;
        const q = arrow.points[i + 1]!;
        const mid = { x: (p.x + q.x) / 2, y: (p.y + q.y) / 2 };
        const next = [...arrow.points.slice(0, i + 1), mid, ...arrow.points.slice(i + 1)];
        patchArrowMut.mutate({ id: arrow.id, patch: { points: next } });
        return;
      }
      // Body or vertex click → select + open popover.
      setCtxMenu(null);
      setSelectedId(null);
      setPopover(null);
      setSelectedArrowId(arrow.id);
      setArrowPopover({ x: clientX, y: clientY, arrow });
      return;
    }
    const obj = objects.find((o) => o.id === id);
    if (!obj) return;
    setCtxMenu(null);
    setSelectedArrowId(null);
    setArrowPopover(null);
    setSelectedId(id);
    setPopover({ x: clientX, y: clientY, object: obj });
  }

  /**
   * Constrains a marker drag position so that the dragged icon's rect does not
   * overlap any other visible icon's rect. Uses the rect size *as currently
   * rendered on screen* (which depends on the view scale).
   */
  function resolveMarkerDragPosition(
    id: string,
    worldX: number,
    worldY: number,
    viewScale: number,
  ): { x: number; y: number } {
    // Arrow vertices/bodies are not square icons → no constraint.
    if (isArrowSyntheticId(id)) return { x: worldX, y: worldY };
    const dragged = objects.find((o) => o.id === id);
    if (!dragged) return { x: worldX, y: worldY };
    const inv = 1 / Math.max(viewScale, 0.0001);
    const iconInv = Math.max(Math.min(inv, 1 / MIN_ICON_SCALE), 1 / MAX_ICON_SCALE);
    const draggedHalf = iconCollisionSize(iconInv, dragged.metadata.scale ?? 1) / 2;
    // Obstacles use their de-overlapped render positions so the snap matches
    // what the user sees on screen.
    const obstacles = visibleObjects
      .filter((o) => o.id !== id)
      .map((o) => {
        const rpos = adjustedPositionsRef.current.get(o.id) ?? o.position;
        return {
          x: rpos.x,
          y: rpos.y,
          halfSize: iconCollisionSize(iconInv, o.metadata.scale ?? 1) / 2,
        };
      });
    return resolveDragCollision(worldX, worldY, draggedHalf, obstacles);
  }

  /** Called when user drags a marker to a new position. */
  function onMarkerMoved(id: string, worldX: number, worldY: number): void {
    if (isArrowSyntheticId(id)) {
      const parsed = parseArrowSyntheticId(id);
      if (!parsed) return;
      if (parsed.kind === 'vertex' && parsed.index !== undefined) {
        const arrow = arrows.find((a) => a.id === parsed.arrowId);
        if (!arrow || !canEditArrow(arrow)) return;
        const idx = parsed.index;
        const next = arrow.points.slice();
        if (idx < 0 || idx >= next.length) return;
        next[idx] = { x: worldX, y: worldY };
        patchArrowMut.mutate({ id: arrow.id, patch: { points: next } });
        setVertexDrag(null);
        return;
      }
      if (parsed.kind === 'body') {
        const drag = bodyDragRef.current;
        const arrow = arrows.find((a) => a.id === parsed.arrowId);
        if (!arrow || !canEditArrow(arrow) || !drag || drag.arrowId !== arrow.id) {
          bodyDragRef.current = null;
          setBodyDragPreview(null);
          return;
        }
        const dx = worldX - drag.startX;
        const dy = worldY - drag.startY;
        const next = drag.originalPoints.map((p) => ({ x: p.x + dx, y: p.y + dy }));
        patchArrowMut.mutate({ id: arrow.id, patch: { points: next } });
        bodyDragRef.current = null;
        setBodyDragPreview(null);
        return;
      }
      return;
    }
    const obj = objects.find((o) => o.id === id);
    if (!obj || !canEditObject(obj)) return;
    patchMut.mutate({ id, patch: { position: { x: worldX, y: worldY } } });
    // Keep the selection on the moved marker.
    setSelectedId(id);
  }

  /** Hit-test for the resize edge zone of the currently selected marker. */
  function onResizeHandleHitTest(
    worldX: number,
    worldY: number,
    scale: number,
  ): { id: string; originX: number; originY: number; startScale: number } | null {
    if (!selectedId) return null;
    const obj = objects.find((o) => o.id === selectedId);
    if (!obj) return null;
    const objKey = findRegionKey(obj.position.x, obj.position.y);
    if (objKey && disabledSet.has(objKey)) return null;
    const iconInv = Math.max(Math.min(1 / scale, 1 / MIN_ICON_SCALE), 1 / MAX_ICON_SCALE);
    const storedScale = obj.metadata.scale ?? 1;
    const halfSize = (32 * iconInv * storedScale) / 2;
    // Resize handle is on the visible icon edge, which is at the adjusted position.
    const rpos = adjustedPositionsRef.current.get(obj.id) ?? obj.position;
    const dx = Math.abs(worldX - rpos.x);
    const dy = Math.abs(worldY - rpos.y);
    // Must be within icon bounds.
    if (dx > halfSize || dy > halfSize) return null;
    // Edge zone = outer EDGE_ZONE_PX screen-pixels of the icon.
    const edgeWorld = EDGE_ZONE_PX * iconInv;
    const innerR = Math.max(halfSize - edgeWorld, halfSize * 0.45);
    // Only the edge strip (not the center) triggers resize.
    if (dx < innerR && dy < innerR) return null;
    return { id: selectedId, originX: rpos.x, originY: rpos.y, startScale: storedScale };
  }

  /** Called when the user finishes resizing a marker. */
  function onMarkerResized(id: string, newScale: number): void {
    const obj = objects.find((o) => o.id === id);
    if (!obj || !canEditObject(obj)) return;
    patchMut.mutate({ id, patch: { metadata: { ...obj.metadata, scale: newScale } } });
  }

  function onStageContextMenu(e: React.MouseEvent<HTMLDivElement>): void {
    if (!atlasRef.current) return;
    e.preventDefault();
    const world = atlasRef.current.clientToWorld(e.clientX, e.clientY);
    setPopover(null);
    setSelectedId(null);
    setCtxMenu({ x: e.clientX, y: e.clientY, worldX: world.x, worldY: world.y });
  }

  return (
    <div className="flex h-screen flex-col bg-slate-950 text-slate-100">
      <MapTopbar
        onOpenHelp={() => setHelpOpen(true)}
        presence={presence}
        foxholeUpdatedAt={foxholeData.data?.fetchedAt ?? null}
        foxholeOffline={foxholeData.data?.offline ?? false}
        searchItems={searchItems}
        onSearchSelect={onSearchSelect}
        assetCount={objects.length}
        operationObjectCount={operationObjectCount}
        operationArrowCount={operationArrowCount}
        viewMode={viewMode}
        onChangeViewMode={setViewMode}
        lastSandboxFlavour={lastSandboxFlavour}
      />
      <div
        ref={stageRef}
        className="relative flex-1 overflow-hidden"
        onContextMenuCapture={onStageContextMenu}
        onMouseMove={(e) => {
          if (!arrowOptions || !atlasRef.current) return;
          const w = atlasRef.current.clientToWorld(e.clientX, e.clientY);
          setCursorWorld(w);
        }}
        onMouseLeave={() => {
          if (cursorWorld) setCursorWorld(null);
        }}
      >
        <FoxholeAtlas
          ref={atlasRef}
          width={size.w}
          height={size.h}
          overlay={overlay}
          showInnerGrid={showGrid}
          mapBackground={mapBackground}
          warLabels={foxholeWarLabels}
          disabledRegionKeys={disabledSet}
          {...(initialViewport.current ? { initialView: initialViewport.current } : {})}
          onViewChange={onViewChange}
          onMapClick={onMapClick}
          onMarkerHitTest={onMarkerHitTest}
          onMarkerClick={onMarkerClick}
          {...(canEdit ? { onMarkerMoved, onResizeHandleHitTest, onMarkerResized, resolveMarkerDragPosition } : {})}
        />
        {mapBackground === 'ink' && (
          <a
            href="https://arkhitecton.itch.io/foxhole-ink-and-frontline"
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-2 left-2 z-10 rounded bg-slate-900/70 px-2 py-1 text-[10px] text-slate-300 backdrop-blur transition hover:text-slate-100"
          >
            Fond de carte « Ink & Frontline » par Arkhitecton
          </a>
        )}
        <MapToolbar
          mapBackground={mapBackground}
          onToggleBackground={() =>
            setMapBackground(mapBackground === 'default' ? 'imm' : 'default')
          }
          onToggleInkBackground={toggleInkBackground}
          showGrid={showGrid}
          onToggleGrid={toggleGrid}
          showSubregions={showSubregions}
          onToggleSubregions={toggleSubregions}
          placeMode={placeMode}
          onChangePlaceMode={setPlaceMode}
          canEdit={canEdit}
          mode={activeMode}
          forbiddenCategories={forbiddenCategoriesForMode}
          forbiddenIconIds={forbiddenIconIdsForMode}
          style={{ left: toolbarLeft }}
          {...(canEdit && activeMode === 'planner' ? { onExportWarPlan: () => setExportOpen(true) } : {})}
          {...(canEdit && activeMode === 'planner' ? { onExportFacBuildMsup: () => setFacExportOpen(true) } : {})}
          {...(canEdit && activeMode === 'planner' ? { onExportMsup: () => setMsupExportOpen(true) } : {})}
          {...(canEdit && activeMode === 'operation' ? { onExportOperation: () => setOperationExportOpen(true) } : {})}
          arrowOptions={arrowOptions}
          arrowDraft={arrowOptions ? { pointCount: draftPoints.length } : null}
          onSetArrowOptions={(next) => {
            if (next === null) {
              setArrowOptions(null);
              setDraftPoints([]);
              setCursorWorld(null);
              return;
            }
            // Arming arrow mode cancels placement / popovers.
            if (placeMode) setPlaceMode(null);
            setPopover(null);
            setSelectedId(null);
            setArrowPopover(null);
            setSelectedArrowId(null);
            // Reset points only when switching kind (not when only color/arrowhead change).
            setArrowOptions((prev) => {
              if (!prev || prev.kind !== next.kind) {
                setDraftPoints([]);
                setCursorWorld(null);
              }
              return next;
            });
          }}
          onConfirmArrowDraft={() => {
            if (!arrowOptions || draftPoints.length < 2) return;
            createArrowMut.mutate({
              kind: arrowOptions.kind,
              points: draftPoints,
              color: arrowOptions.color,
              arrowheadStart: arrowOptions.arrowheadStart,
              arrowheadEnd: arrowOptions.arrowheadEnd,
              dashed: arrowOptions.dashed,
              mode: activeMode,
            });
            setArrowOptions(null);
            setDraftPoints([]);
            setCursorWorld(null);
          }}
          onCancelArrowDraft={() => {
            setArrowOptions(null);
            setDraftPoints([]);
            setCursorWorld(null);
          }}
          forbiddenArrowCategories={forbiddenCategoriesForMode}
        />
        {ctxMenu && (() => {
          const ctxRegionKey = findRegionKey(ctxMenu.worldX, ctxMenu.worldY);
          const ctxHexDisabled = ctxRegionKey ? disabledSet.has(ctxRegionKey) : false;
          return (
          <MapContextMenu
            x={ctxMenu.x}
            y={ctxMenu.y}
            mode={activeMode}
            canEdit={canEdit && !ctxHexDisabled}
            canToggleHex={(sandboxActive ? !!account : isAdmin) && ctxRegionKey !== null}
            forbiddenCategories={forbiddenCategoriesForMode}
            forbiddenIconIds={forbiddenIconIdsForMode}
            hexIsDisabled={ctxHexDisabled}
            onToggleHex={() => {
              if (!ctxRegionKey) return;
              if (sandboxActive) {
                toggleSandboxRegion(ctxRegionKey);
                return;
              }
              const next = ctxHexDisabled
                ? Array.from(disabledSet).filter((k) => k !== ctxRegionKey)
                : Array.from(new Set([...disabledSet, ctxRegionKey]));
              updateSettingsMut.mutate({ disabledRegionKeys: next });
            }}
            onClose={() => setCtxMenu(null)}
            onPick={(category: IconCategory, iconId) => {
              createMut.mutate({
                kind: category,
                position: { x: ctxMenu.worldX, y: ctxMenu.worldY },
                metadata: { iconId },
                mode: activeMode,
              });
              setCtxMenu(null);
            }}
          />
          );
        })()}
        {popover && (
          <MapObjectPopover
            x={popover.x}
            y={popover.y}
            object={popover.object}
            canEdit={canEditObject(popover.object)}
            forbiddenCategories={forbiddenCategoriesForMode}
            forbiddenIconIds={forbiddenIconIdsForMode}
            onClose={() => {
              setPopover(null);
              setSelectedId(null);
            }}
            onPatch={(patch) => patchMut.mutate({ id: popover.object.id, patch })}
            onDelete={() => setPendingDelete(popover.object.id)}
          />
        )}
        {arrowPopover && (
          <MapArrowPopover
            x={arrowPopover.x}
            y={arrowPopover.y}
            arrow={arrowPopover.arrow}
            canEdit={canEditArrow(arrowPopover.arrow)}
            forbiddenCategories={forbiddenCategoriesForMode}
            onClose={() => {
              setArrowPopover(null);
              setSelectedArrowId(null);
            }}
            onPatch={(patch) =>
              patchArrowMut.mutate({ id: arrowPopover.arrow.id, patch })
            }
            onDelete={() => setPendingDeleteArrow(arrowPopover.arrow.id)}
          />
        )}
        {pendingDeleteArrow && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-arrow-title"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setPendingDeleteArrow(null);
            }}
          >
            <div className="w-80 rounded border border-slate-700 bg-slate-900 p-5 shadow-2xl">
              <p id="confirm-delete-arrow-title" className="mb-4 text-sm font-semibold text-slate-100">
                Supprimer cette flèche ?
              </p>
              <p className="mb-5 text-xs text-slate-400">Cette action est irréversible.</p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPendingDeleteArrow(null)}
                  className="rounded bg-slate-700 px-3 py-1.5 text-xs text-slate-100 hover:bg-slate-600"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => {
                    deleteArrowMut.mutate(pendingDeleteArrow);
                    setPendingDeleteArrow(null);
                    setArrowPopover(null);
                    setSelectedArrowId(null);
                  }}
                  className="rounded bg-red-700 px-3 py-1.5 text-xs text-white hover:bg-red-600"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
        {pendingDelete && (
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="confirm-delete-title"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
            onMouseDown={(e) => { if (e.target === e.currentTarget) setPendingDelete(null); }}
          >
            <div className="w-80 rounded border border-slate-700 bg-slate-900 p-5 shadow-2xl">
              <p id="confirm-delete-title" className="mb-4 text-sm font-semibold text-slate-100">
                Supprimer cet objet ?
              </p>
              <p className="mb-5 text-xs text-slate-400">
                Cette action est irréversible.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setPendingDelete(null)}
                  className="rounded bg-slate-700 px-3 py-1.5 text-xs text-slate-100 hover:bg-slate-600"
                >
                  Annuler
                </button>
                <button
                  type="button"
                  onClick={() => {
                    deleteMut.mutate(pendingDelete);
                    setPendingDelete(null);
                    setPopover(null);
                    setSelectedId(null);
                  }}
                  className="rounded bg-red-700 px-3 py-1.5 text-xs text-white hover:bg-red-600"
                >
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        )}
        {activeMode === 'planner' ? (
          <WarPlanPanel canEdit={sandboxActive ? canEditSandbox : account?.role === 'admin'} />
        ) : (
          <OperationPlanPanel canEdit={canEdit} />
        )}
        <AssetListPanel
          objects={objects}
          arrows={arrows}
          warLabels={foxholeWarLabels}
          disabledRegionKeys={disabledSet}
          mode={activeMode}
          onNavigate={(wx, wy) => {
            atlasRef.current?.setView({ cx: wx, cy: wy, scale: 2.5 });
          }}
        />
        {activeMode === 'planner' && (
          <>
            <WarPlanExportModal
              open={exportOpen}
              onClose={() => setExportOpen(false)}
              objects={objects}
              arrows={arrowsForExports}
              disabledRegionKeys={disabledSet}
              mapBackground={mapBackground}
              warPlan={warPlanQuery.data ?? null}
              warLabels={foxholeWarLabels}
            />
            <FacBuildMsupExportModal
              open={facExportOpen}
              onClose={() => setFacExportOpen(false)}
              objects={objects}
              arrows={arrowsForExports}
              disabledRegionKeys={disabledSet}
              mapBackground={mapBackground}
              warLabels={foxholeWarLabels}
              warPlan={warPlanQuery.data ?? null}
              scope="fac-build"
            />
            <FacBuildMsupExportModal
              open={msupExportOpen}
              onClose={() => setMsupExportOpen(false)}
              objects={objects}
              arrows={arrowsForExports}
              disabledRegionKeys={disabledSet}
              mapBackground={mapBackground}
              warLabels={foxholeWarLabels}
              warPlan={warPlanQuery.data ?? null}
              scope="msup"
            />
          </>
        )}
        {activeMode === 'operation' && (
          <OperationExportModal
            open={operationExportOpen}
            onClose={() => setOperationExportOpen(false)}
            objects={objects}
            arrows={arrowsForExports}
            disabledRegionKeys={disabledSet}
            mapBackground={mapBackground}
            warLabels={foxholeWarLabels}
          />
        )}
        {sandboxActive && <SandboxNotification />}
      </div>
      <HelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
